import { TextField, MenuItem } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { UseFormRegisterReturn } from 'react-hook-form'

import useWorldCountries from '@app/components/CountriesSelector/hooks/useWorldCountries'
import { useScopedTranslation } from '@app/hooks/useScopedTranslation'

import { australiaRegions, newZealandRegions } from '../../utils/ANZ'

interface IRegionDropdownProps {
  countryCode: string
  enableOther?: boolean
  error: boolean
  errormessage?: string
  register: UseFormRegisterReturn
  required: boolean
  setValue?: (value: string | null) => void
  value: string | null
  displayVirtualOption?: boolean
}
export const RegionSelector = React.forwardRef(function RegionSelector(
  props: IRegionDropdownProps,
) {
  const {
    countryCode,
    enableOther,
    error,
    errormessage,
    register,
    required,
    setValue,
    value,
    displayVirtualOption,
  } = props

  const { isAustraliaCountry, isNewZealandCountry } = useWorldCountries()
  const { t } = useScopedTranslation('components.organisation-region')
  const isAustralia = isAustraliaCountry(countryCode)
  const isNewZeeland = isNewZealandCountry(countryCode)

  const options = isAustralia ? australiaRegions : newZealandRegions

  const optionsContainingVirtual = useMemo(() => {
    return displayVirtualOption ? [...options, t('virtual')] : options
  }, [displayVirtualOption, options, t])

  useEffect(() => {
    if (value && !optionsContainingVirtual.includes(value)) {
      setValue?.(null)
    }
  }, [countryCode, optionsContainingVirtual, setValue, value])

  if (enableOther && !optionsContainingVirtual.includes('Other')) {
    optionsContainingVirtual.push('Other')
  }

  if (!isAustralia && !isNewZeeland) {
    return null
  }

  const defaultLabel = isAustralia ? 'stateTerritory' : 'region'

  return (
    <TextField
      sx={{ bgcolor: 'grey.100' }}
      select
      fullWidth
      variant="filled"
      required={required}
      error={error}
      helperText={errormessage}
      label={t(enableOther ? 'postal-address-state-territory' : defaultLabel)}
      {...register}
      value={value}
      defaultValue={value}
      data-testid="region-selector"
    >
      {optionsContainingVirtual?.length ? (
        optionsContainingVirtual.map(option => (
          <MenuItem
            key={option}
            value={option}
            data-testid={`region-option-${option}`}
          >
            {option}
          </MenuItem>
        ))
      ) : (
        <MenuItem value="" disabled>
          {t('region')}
        </MenuItem>
      )}
    </TextField>
  )
})
